import { useState } from 'react';
import { CatalogSectionSortCriterionEnum } from '../../../../../enums/catalogSectionSortCriterion.enum';
import {
  CATALOG_VIEW_CATALOG_FIELDS,
  CATALOG_VIEW_USER_FIELDS,
  DEFAULT_CATALOG_VIEW,
} from '../../../view/catalogView.consts';
import {
  CatalogViewCatalogField,
  CatalogViewCatalogFields,
  CatalogViewDraft,
  CatalogViewGroupBy,
  CatalogViewUserField,
} from '../../../view/catalogView.types';
import { getIsViewCombinationValid } from '../../../view/resolveViewCombination';
import {
  ApplyFieldChangeParams,
  BuildDraftParams,
  CatalogViewDraftField,
  UseCatalogViewDraftParams,
} from './useCatalogViewDraft.types';

const buildDraft = ({ applied }: BuildDraftParams): CatalogViewDraft => ({
  mode: applied.mode,
  cardSize: applied.cardSize,
  groupBy: applied.groupBy,
  groupTargetId: applied.groupTargetId,
  sortCriterion: applied.sortCriterion,
  sortTargetId: applied.sortTargetId,
  isRestorePending: false,
});

const applyFieldChange = <K extends CatalogViewDraftField>({
  draft,
  field,
  value,
}: ApplyFieldChangeParams<K>): CatalogViewDraft => {
  if (draft[field] === value) {
    return draft;
  }

  const next: CatalogViewDraft = { ...draft, [field]: value };

  if (CATALOG_VIEW_USER_FIELDS.some((userField) => userField === field)) {
    next.isRestorePending = false;
  }

  if (field === 'sortCriterion' && value !== CatalogSectionSortCriterionEnum.CLASSIFICATION_TYPE) {
    next.sortTargetId = null;
  }

  if (field === 'groupBy' && value !== CatalogViewGroupBy.CLASSIFICATION_TYPE) {
    next.groupTargetId = null;
  }

  return next;
};

const getIsSortTargetMissing = (draft: CatalogViewDraft): boolean =>
  draft.sortCriterion === CatalogSectionSortCriterionEnum.CLASSIFICATION_TYPE &&
  draft.sortTargetId === null;

const getIsGroupTargetMissing = (draft: CatalogViewDraft): boolean =>
  draft.groupBy === CatalogViewGroupBy.CLASSIFICATION_TYPE && draft.groupTargetId === null;

const getIsCombinationDirty = (dirtyFields: CatalogViewCatalogField[]): boolean =>
  dirtyFields.includes('sortCriterion') || dirtyFields.includes('groupBy');

export const useCatalogViewDraft = ({
  applied,
  catalogConfigLive,
  hasSavedUserPreference,
  isPreferencesSettled,
  isCatalogSettled,
  isApplyForAllEnabled,
}: UseCatalogViewDraftParams) => {
  const [configSnapshot, setConfigSnapshot] = useState<CatalogViewCatalogFields | null>(null);
  const [draft, setDraft] = useState<CatalogViewDraft | null>(null);

  if (!draft && isCatalogSettled && isPreferencesSettled) {
    const snapshot = catalogConfigLive ?? DEFAULT_CATALOG_VIEW;
    setConfigSnapshot(snapshot);
    setDraft(buildDraft({ applied }));
  }

  const dirtyForMe: CatalogViewUserField[] = draft
    ? CATALOG_VIEW_USER_FIELDS.filter((field) => draft[field] !== applied[field])
    : [];

  const dirtyForAll: CatalogViewCatalogField[] =
    draft && configSnapshot
      ? CATALOG_VIEW_CATALOG_FIELDS.filter((field) => draft[field] !== configSnapshot[field])
      : [];

  const hasPreferenceWrite = dirtyForMe.length > 0 || !!draft?.isRestorePending;

  const isCombinationInvalid =
    !!draft &&
    !getIsViewCombinationValid({ sortCriterion: draft.sortCriterion, groupBy: draft.groupBy });
  const isCombinationBlockedForMe =
    isCombinationInvalid && !draft?.isRestorePending && getIsCombinationDirty(dirtyForMe);
  const isCombinationBlockedForAll = isCombinationInvalid && getIsCombinationDirty(dirtyForAll);

  const isSortTargetMissing = !!draft && getIsSortTargetMissing(draft);
  const isGroupTargetMissing = !!draft && getIsGroupTargetMissing(draft);

  const canApplyForMe =
    !!draft &&
    hasPreferenceWrite &&
    isPreferencesSettled &&
    !isCombinationBlockedForMe &&
    !isSortTargetMissing &&
    !isGroupTargetMissing;

  const canApplyForAll =
    !!draft &&
    isApplyForAllEnabled &&
    isCatalogSettled &&
    !isCombinationBlockedForAll &&
    dirtyForAll.length > 0 &&
    !isSortTargetMissing &&
    !isGroupTargetMissing;

  const canRestore = !!draft && (dirtyForMe.length > 0 || hasSavedUserPreference);

  const restore = () => {
    if (!configSnapshot) {
      return;
    }

    setDraft({ ...configSnapshot, isRestorePending: true });
  };

  const setField = <K extends CatalogViewDraftField>(field: K, value: CatalogViewDraft[K]) => {
    if (!draft) {
      return;
    }

    setDraft(applyFieldChange({ draft, field, value }));
  };

  return {
    draft,
    configSnapshot,
    dirtyForMe,
    dirtyForAll,
    isCombinationInvalid: isCombinationBlockedForMe,
    isSortTargetMissing,
    isGroupTargetMissing,
    canApplyForMe,
    canApplyForAll,
    canRestore,
    restore,
    setField,
  };
};
